"use client"

import { useState, useEffect } from "react"
import { useAuth } from "@clerk/nextjs"
import { motion, AnimatePresence } from "framer-motion"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Share2, Plus, Check, Loader2, X, Bell } from "lucide-react"
import { isIOSDevice, isInstalledPWA } from "@/lib/push-notifications"
import { canShowWizardNow, trackWizardDismissal, trackWizardCompletion, shouldShowIOSWizard } from "@/lib/ios-install-wizard"
import { subscribeToPushNotifications } from "@/lib/push-notifications"
import { useToast } from "@/components/ui/use-toast"
import { onWizardTrigger } from "@/lib/wizard-trigger"

interface IOSInstallWizardProps {
  autoShow?: boolean
  delay?: number // Delay before showing wizard in ms (default: 10000 = 10 seconds)
  trigger?: 'auto' | 'post-entry' // Trigger type for contextual display
}

export function IOSInstallWizard({ autoShow = true, delay = 10000, trigger = 'auto' }: IOSInstallWizardProps) {
  const { userId } = useAuth()
  const { toast } = useToast()
  const [isOpen, setIsOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [isDetecting, setIsDetecting] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [preferences, setPreferences] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Fetch user preferences
  useEffect(() => {
    async function fetchPreferences() {
      if (!userId) return

      try {
        const response = await fetch("/api/user/preferences")
        if (response.ok) {
          const prefs = await response.json()
          setPreferences(prefs)
        }
      } catch (error) {
        console.error("[Wizard] Failed to fetch preferences:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchPreferences()
  }, [userId])

  // Auto-show wizard logic
  useEffect(() => {
    if (!autoShow || isLoading) return
    if (!userId) return
    if (!isIOSDevice()) return
    if (isInstalledPWA()) {
      // Already installed, mark as completed
      handleInstallDetected()
      return
    }

    // Check if already shown this session
    const shownThisSession = sessionStorage.getItem('ios-wizard-shown-this-session')
    if (shownThisSession === 'true' && trigger === 'auto') {
      // For auto-trigger, only show once per session
      return
    }

    // Check if should show based on preferences
    if (!shouldShowIOSWizard(preferences)) return

    // Check localStorage for immediate feedback
    if (!canShowWizardNow()) return

    // Show wizard after delay
    const timer = setTimeout(() => {
      setIsOpen(true)
      // Mark as shown this session
      sessionStorage.setItem('ios-wizard-shown-this-session', 'true')
    }, delay)

    return () => clearTimeout(timer)
  }, [autoShow, delay, userId, isLoading, preferences, trigger])

  // Listen for programmatic triggers (e.g., after entry save)
  useEffect(() => {
    const cleanup = onWizardTrigger((triggerDelay, force) => {
      // Check basic conditions
      if (!userId) return
      if (!isIOSDevice()) return
      if (isInstalledPWA()) return

      // If forced, skip session check
      if (force) {
        setTimeout(() => {
          setIsOpen(true)
          sessionStorage.setItem('ios-wizard-shown-this-session', 'true')
        }, triggerDelay)
        return
      }

      // Check if already shown this session
      const shownThisSession = sessionStorage.getItem('ios-wizard-shown-this-session')
      if (shownThisSession === 'true') return

      // Check if should show based on preferences
      if (!shouldShowIOSWizard(preferences)) return
      if (!canShowWizardNow()) return

      // Show after delay
      setTimeout(() => {
        setIsOpen(true)
        sessionStorage.setItem('ios-wizard-shown-this-session', 'true')
      }, triggerDelay)
    })

    return cleanup
  }, [userId, preferences])

  // Handle wizard dismissal
  const handleDismiss = async () => {
    if (!userId || !preferences) {
      setIsOpen(false)
      return
    }

    try {
      const { dismissCount, dismissedUntil, isPermanentOptOut } = trackWizardDismissal(
        preferences.iosInstallWizardDismissCount || 0
      )

      // Update database
      await fetch("/api/user/preferences", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          iosInstallWizardShown: true,
          iosInstallWizardDismissCount: dismissCount,
          iosInstallWizardLastDismissed: dismissedUntil.toISOString(),
          iosInstallWizardPermanentlyDismissed: isPermanentOptOut,
        }),
      })

      // Show permanent opt-out confirmation if 4th+ dismissal
      if (isPermanentOptOut) {
        toast({
          title: "Průvodce instalací skryt",
          description: "Zobrazí se znovu za 30 dní. Můžeš ho kdykoliv zapnout v nastavení.",
        })
      }

      setIsOpen(false)
      setCurrentStep(1)
    } catch (error) {
      console.error("[Wizard] Failed to track dismissal:", error)
      setIsOpen(false)
    }
  }

  // Handle install detected
  const handleInstallDetected = async () => {
    if (!userId) return

    try {
      trackWizardCompletion()

      await fetch("/api/user/preferences", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          iosInstallWizardCompleted: true,
        }),
      })

      setIsInstalled(true)
    } catch (error) {
      console.error("[Wizard] Failed to track completion:", error)
    }
  }

  // Poll for PWA installation (Step 3)
  useEffect(() => {
    if (currentStep !== 3 || !isDetecting) return

    let pollCount = 0
    const maxPolls = 30 // 30 polls * 2 seconds = 60 seconds

    const pollInterval = setInterval(() => {
      pollCount++

      if (isInstalledPWA()) {
        clearInterval(pollInterval)
        setIsDetecting(false)
        handleInstallDetected()
        setCurrentStep(4) // Success step
      } else if (pollCount >= maxPolls) {
        clearInterval(pollInterval)
        setIsDetecting(false)
        toast({
          title: "Ještě je to potřeba dokončit",
          description: "Pokud jsi aplikaci přidal/a na plochu, otevři ji odtud a vrať se zpět.",
          duration: 5000,
        })
      }
    }, 2000)

    return () => clearInterval(pollInterval)
  }, [currentStep, isDetecting])

  // Handle push notification setup
  const handleEnablePush = async () => {
    try {
      await subscribeToPushNotifications()
      toast({
        title: "Notifikace zapnuty",
        description: "Budeš dostávat denní připomínky.",
      })
      setIsOpen(false)
    } catch (error) {
      console.error("[Wizard] Failed to enable push:", error)
      toast({
        title: "Chyba",
        description: "Nepodařilo se zapnout notifikace. Zkus to později v nastavení.",
        variant: "destructive",
      })
    }
  }

  // Don't render on non-iOS devices or if already installed
  if (!isIOSDevice() || (isInstalledPWA() && !isInstalled)) return null

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {currentStep === 1 && "Přidej si Vnitřní Mapu na plochu"}
            {currentStep === 2 && "Jak přidat aplikaci"}
            {currentStep === 3 && "Čekáme na instalaci..."}
            {currentStep === 4 && "Skvělé! Aplikace je nainstalovaná ✓"}
          </DialogTitle>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {/* Step 1: Value Proposition */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-sm">Denní push připomínky (ráno a večer)</p>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-sm">Rychlejší přístup z plochy telefonu</p>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-sm">Funguje offline - zápis kdykoli</p>
                </div>
              </div>

              <div className="flex flex-col gap-2 pt-4">
                <Button onClick={() => setCurrentStep(2)} className="w-full">
                  Začít instalaci
                </Button>
                <div className="flex gap-2">
                  <Button onClick={handleDismiss} variant="outline" className="flex-1">
                    Později
                  </Button>
                  <Button onClick={handleDismiss} variant="ghost" size="sm">
                    Ne, díky
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Installation Instructions */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold flex-shrink-0">
                    1
                  </div>
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                      <Share2 className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm font-medium">Klikni na tlačítko "Sdílet"</p>
                    </div>
                    <p className="text-xs text-muted-foreground">Dole uprostřed prohlížeče Safari</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold flex-shrink-0">
                    2
                  </div>
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                      <Plus className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm font-medium">Vyber "Přidat na plochu"</p>
                    </div>
                    <p className="text-xs text-muted-foreground">Sroluj dolů v menu</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold flex-shrink-0">
                    3
                  </div>
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm font-medium">Potvrď přidáním</p>
                    </div>
                    <p className="text-xs text-muted-foreground">Pravý horní roh</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 pt-4">
                <Button
                  onClick={() => {
                    setCurrentStep(3)
                    setIsDetecting(true)
                  }}
                  className="w-full"
                >
                  Rozumím, přidávám
                </Button>
                <div className="flex gap-2">
                  <Button onClick={() => setCurrentStep(1)} variant="outline" className="flex-1">
                    Předchozí krok
                  </Button>
                  <Button onClick={handleDismiss} variant="ghost" size="sm">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Detection */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <div className="flex flex-col items-center justify-center py-8 space-y-4">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
                <div className="text-center space-y-2">
                  <p className="text-sm font-medium">Čekáme na instalaci...</p>
                  <p className="text-xs text-muted-foreground">Otevři aplikaci z plochy telefonu a vrať se sem.</p>
                  <p className="text-xs text-muted-foreground">Detekce probíhá automaticky.</p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Button onClick={handleDismiss} variant="outline" className="w-full">
                  Nastavím to později
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Success */}
          {currentStep === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <div className="flex flex-col items-center justify-center py-8 space-y-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <Check className="h-8 w-8 text-primary" />
                </div>
                <div className="text-center space-y-2">
                  <p className="text-lg font-semibold">Skvělé! Aplikace je nainstalovaná</p>
                  <p className="text-sm text-muted-foreground">Teď můžeš zapnout push notifikace pro denní připomínky.</p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Button onClick={handleEnablePush} className="w-full">
                  <Bell className="mr-2 h-4 w-4" />
                  Zapnout notifikace
                </Button>
                <Button
                  onClick={() => {
                    setIsOpen(false)
                    toast({
                      title: "Můžeš to zapnout později",
                      description: "Notifikace najdeš v nastavení aplikace.",
                    })
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Udělám později
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Progress indicator */}
        {currentStep <= 3 && (
          <div className="flex justify-center gap-2 pt-4">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`h-1.5 w-8 rounded-full transition-colors ${
                  step === currentStep ? "bg-primary" : step < currentStep ? "bg-primary/50" : "bg-muted"
                }`}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
